import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { INode, ThresholdDisplayedSubNodes } from '../Configuration/INode';
import { IAttribute } from '../attribute/IAttribute';
import { OxeModelService } from '../oxe-model-service/oxe-model.service';
import { DialogService } from '../confirm-dialog/dialog.service';

@Component({
  selector: 'wbm-config-filter',
  templateUrl: './config-filter.component.html',
  styleUrls: ['../../assets/theme/css/ConfigFilter.css']
})
export class ConfigFilterComponent implements OnInit {
  private _node: INode;
  private _allFilterableAttributes: Array<IAttribute>;
  private _filterableAttributes: Array<IAttribute>;
  private formGroup: FormGroup;
  object = Object;
  isDisplayedSubNodesLimitEnabled: boolean; // !important! do not initialize

  @Input()
  set node(node: INode) {
    this._node = node;

    this._allFilterableAttributes = node.getFilteringAttributes();

    // Retrieve the list of attributes that are filterable and sortable, and limit this list to 5 attributes
    this._filterableAttributes = this._allFilterableAttributes.slice(0, 5);

    // initialize our form
    if (!!this.formGroup) {
      // If formGroup has already been instanciated, we remove all filterArray inside it before replacing them with new ones
      for (let i = this.filters.length - 1; i > -1; i--) {
        this.filters.removeAt(i);
      }
    } else {
      this.formGroup = this._fb.group({ 'and': this._fb.array([]) });
    }
    // Populate formGroup with new formControls
    this.getFiltersControls();
  }

  get node(): INode {
    return this._node;
  }

  get filterableAttributes(): Array<IAttribute> {
    return this._filterableAttributes || [];
  }

  get allFilterableAttributes(): Array<IAttribute> {
    return this._allFilterableAttributes || [];
  }

  get filters(): FormArray {
    return this.formGroup.get('and') as FormArray;
  }

  get shouldHaveDisplayLimit(): boolean {
    return this.node.subNodes && this.node.subNodes.length > this.node.maxDisplayedSubNodes;
  }

  get shouldDisableDisplayAll(): boolean {
    return this.node.subNodes && this.node.subNodesInitialLength > ThresholdDisplayedSubNodes;
  }

  get hasActivatedControl(): boolean {
    return this.filters.controls.some(ctrl => ctrl.enabled);
  }

  constructor(private _fb: FormBuilder, private _oxeModel: OxeModelService, private _dialogService: DialogService) { }

  ngOnInit() {
  }

  onDisplayLimitStateChanged(isChecked: boolean, target: any) {
    if (!isChecked && this.shouldDisableDisplayAll) {
      this._dialogService.confirm('display-all-confirm')
        .then(() => this.isDisplayedSubNodesLimitEnabled = isChecked)
        .catch(() => target.checked = true);
    } else {
      this.isDisplayedSubNodesLimitEnabled = isChecked;
    }
  }

  onDisplayLimitValueChanged(limit: string) {
    this.node.maxDisplayedSubNodes = Number.parseInt(limit);
  }

  onFilterStateChanged(isChecked: boolean, filterIndex: number) {
    if (isChecked) {
      this.filters.controls[filterIndex].enable();
      //save filter data
      const newFilter = this.filters.controls[filterIndex].value;
      this.node.setFilterCondition({ 'and': [newFilter] }, filterIndex, true);
    } else {
      this.filters.controls[filterIndex].disable();
      //save filter data
      const newFilter = this.filters.controls[filterIndex].value;
      this.node.setFilterCondition({ 'and': [newFilter] }, filterIndex, false);
    }
  }

  getFiltersControls(): void {
    for (let index = 0; index < this._filterableAttributes.length; index++) {
      if (!this.node.currentFilters || this.node.currentFilters[index] === undefined) {
        //no current filter saved for this index: create filter control from _filterableAttributes array
        const attribute = this._filterableAttributes[index];
        if (attribute.isSequence || attribute.isSetOf) {
          this.filters.push(this._fb.control({ value: { 'Contains': [{ 'var': attribute.name }, ''] }, disabled: true }));
        } else if (attribute.isBoolean){
          this.filters.push(this._fb.control({ value: { '==': [{ 'var': attribute.name }, !!attribute.defaultValue ? attribute.defaultValue : 'false'] }, disabled: true }));
        } else {
          this.filters.push(this._fb.control({ value: { '==': [{ 'var': attribute.name }, !!attribute.defaultValue ? attribute.defaultValue : ''] }, disabled: true }));
        }
      } else {
        //current filter exists in node for this index: use the currentFilters saved data to create the control for this index
        let filterData = this.node.currentFilters[index];
        const enabled = filterData['enabled'];
        let controlObject = {};
        const operator = filterData['operator'] as string;
        const name = filterData['name'] as string;
        const value = filterData['value'];
        controlObject[operator] = [{ 'var': name }, value];

        this.filters.push(this._fb.control(
          {
            value: controlObject,
            disabled: !enabled
          }
        ));
      }
    }
  }

  private getAttributesToRetrieve(): Array<IAttribute>  {
    let attributesToRetrieve: Array<IAttribute> = [];

    for (let index = 0; index < this._filterableAttributes.length; index++) {
      if (!this.node.currentFilters || this.node.currentFilters[index] === undefined) {
        //no current filter saved for this index: push the attribute corresponding to this index in _filterableAttributes
        attributesToRetrieve.push(this._filterableAttributes[index]);
      } else {
        //current filter exists in saved filter data for this node at this index: use the currentFilter name at this index to push the attribute corresponding to this name
        let filterData = this.node.currentFilters[index];
        const name = filterData['name'] as string;
        attributesToRetrieve.push(this.node.getAttributeByName(name));
      }
    }
    // remove duplicates
    attributesToRetrieve = Array.from(new Set(attributesToRetrieve));

    return attributesToRetrieve;
  }

  onSubmit() {

    if (this.formGroup.disabled) {
      this.node.resetFilterCondition(true); //true to keep current filters containing possible name changes
    } else {
      this.node.setFilterCondition(this.formGroup.value);
    }
    /* if (this.isDisplayedSubNodesLimitEnabled !== undefined) {
      this.node.shouldApplyLimit = this.isDisplayedSubNodesLimitEnabled;
    }*/
    this.node.shouldApplyLimit = true; // applying limit can't be disabled

    // update initialAttributes with the list of attributes in filter form
    let attributesToRetrieve = this.getAttributesToRetrieve();

    this.node.setInitialAttributes(attributesToRetrieve);

    // retrieve subnodes before filtering
    this._oxeModel.retrieveData(this.node)
      .subscribe(() => {
        this.node.isExpanded = true;
        this.node.filterApplied(); //in order to refresh tree view
      });
  }

  onReset() {
    // reset limit and filters and sort
    this.node.resetDisplayedSubNodesLimit();
    this.node.resetFilterCondition();
    this.node.deleteAllSortCriteria();

    // re-initialize our form
    let attributesNameList = {};
    this.filterableAttributes.forEach((attribute, i) => {
      attributesNameList[attribute.name] = attribute.structure;
      if (attribute.isSequence || attribute.isSetOf) {
        this.filters.controls[i].setValue({ 'Contains': [{ 'var': attribute.name }, ''] });
      } else if(attribute.isBoolean){
        this.filters.controls[i].setValue({ '==': [{ 'var': attribute.name }, !!attribute.defaultValue ? attribute.defaultValue : 'false'] });
      } else {
        this.filters.controls[i].setValue({ '==': [{ 'var': attribute.name }, !!attribute.defaultValue ? attribute.defaultValue : ''] });
      }
      this.filters.controls[i].disable();
    });

    // apply to reset filtering
    this.onSubmit();
  }

  onInputModified(event: any, filterIndex: number) {
    //The reset of sortorder data (if resetSortOrder property is present in event) must be executed before the filter condition saving
    const resetSortOrder = event.resetSortOrder;
    const enabled = event.enabled;
    if (resetSortOrder !== undefined && resetSortOrder) {
        this.removeExistingSortCriteria(filterIndex);
    }

    //build and save filter condition
    let newFilter = {};
    newFilter[event.operator] = [{ 'var': event.name }, event.value];
    this.node.setFilterCondition({ 'and': [newFilter] }, filterIndex, enabled);
    if (enabled !== undefined) {
      if (enabled) {
        this.filters.controls[filterIndex].enable();
      } else {
        this.filters.controls[filterIndex].disable();
      }
    }
  }

  changeSort(filterIndex: number): void {
    let attribute = this.getAttributeFromFilterIndex(filterIndex);
    this.node.toggleSortCriteria(attribute);
    this._oxeModel.sortDisplayedSubNodes(this.node);
  }

  getSortCriteria(filterIndex: number) {
    let attribute = this.getAttributeFromFilterIndex(filterIndex);
    return this.node.getSortCriteria(attribute);
  }

  private getAttributeFromFilterIndex(filterIndex: number): IAttribute {
    //filterIndex is the index in this.filterableAttributes array , or in this.node.currentFilters object if the attribute filter at this index has already been modified
    let attribute;
    if (!this.node.currentFilters || this.node.currentFilters[filterIndex] === undefined) {
        //no current filter saved for this index: use the attribute corresponding to this index in _filterableAttributes
        attribute = this._filterableAttributes[filterIndex];
      } else {
        //current filter exists in saved filter data for this node at this index: use the currentFilter name at this index to push the attribute corresponding to this name
        let filterData = this.node.currentFilters[filterIndex];
        const attributeName = filterData['name'] as string;
        attribute = this.node.getAttributeByName(attributeName);
    }
    return attribute;
  }

  private removeExistingSortCriteria(filterIndex: number) {
    let attribute = this.getAttributeFromFilterIndex(filterIndex);
    this.node.deleteSortCriteria(attribute);
  }
}
